// registerSW — admin variant. Mirrors the user-app polling + auto-update
// flow so workers don't have to swipe-kill the PWA to pick up new
// builds. Cloudflare's _headers file (apps/admin/public/_headers) also
// flips /sw.js to no-cache so the polled update() actually sees fresh
// SW bytes from origin instead of CF's 4-hour edge cache.

import { registerSW } from "virtual:pwa-register";

// 10s (was 60s) for activity day: a worker who keeps the admin PWA open
// picks up a mid-activity redeploy within seconds instead of up to a
// minute. Only polls while visible; iOS background-frozen PWAs still
// update on the next foreground return via visibilitychange below.
const POLL_INTERVAL_MS = 10_000;

export function startSW(): void {
  if (typeof window === "undefined") return;
  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      const tick = () => {
        if (document.visibilityState !== "visible") return;
        registration.update().catch(() => {});
      };
      window.setInterval(tick, POLL_INTERVAL_MS);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") tick();
      });
    },
    onNeedRefresh() {
      // Auto-apply + reload (no toast). When a worker is mid-pin-placement a
      // reload is mildly annoying but never destructive — pending pin state
      // is local-only.
      //
      // CRITICAL (2026-05-30 fix): clear the workbox precache before reload.
      // Without this, admin hit the same "new index.html + stale chunk
      // filenames" race the user app already fixed (see apps/user/registerSW.ts):
      // the new SW activated but the OLD precache kept serving stale JS chunks,
      // so updateSW(true) reloaded into a half-old app — the worker had to
      // swipe-kill the PWA to actually get the new build. Dropping the
      // workbox precache (NOT the asset caches) forces fresh chunks on reload.
      const swap = async () => {
        try {
          if ("caches" in self) {
            const keys = await caches.keys();
            // workbox precache key looks like "workbox-precache-..."; drop
            // those so the new index's chunk references resolve fresh.
            await Promise.all(
              keys
                .filter((k) => k.includes("precache") || k.includes("workbox"))
                .map((k) => caches.delete(k)),
            );
          }
        } catch {
          /* ignore — reload anyway */
        }
        await updateSW(true);
      };
      void swap();
    },
    onOfflineReady() {
      /* no-op */
    },
  });
}
